import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, eur, kindLabel, statusLabel, API } from "@/lib/api";
import { toast } from "sonner";
import { FileDown, Search } from "lucide-react";

const STATUS_STYLES = {
  pending_payment: "text-yellow-300 border-yellow-500/40",
  awaiting_validation: "text-yellow-300 border-yellow-500/40",
  confirmed: "text-emerald-300 border-emerald-500/40",
  refused: "text-red-300 border-red-500/40",
  in_progress: "text-blue-300 border-blue-500/40",
  completed: "text-emerald-300 border-emerald-500/40",
  cancelled: "text-gray-400 border-gray-500/40",
};

export default function TrackBooking() {
  const { reference: refParam } = useParams();
  const navigate = useNavigate();
  const [ref, setRef] = useState(refParam || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (refParam) fetchRef(refParam);
    // eslint-disable-next-line
  }, [refParam]);

  const fetchRef = async (r) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/bookings/track/${r.trim().toUpperCase()}`);
      setData(data);
    } catch (e) {
      toast.error("Aucune réservation trouvée");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (ref.trim()) {
      navigate(`/suivi/${ref.trim().toUpperCase()}`);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="track-page">
      <Header />
      <div className="pt-32 pb-20 max-w-3xl mx-auto px-6 lg:px-10">
        <div className="text-xs tracking-[0.5em] gold uppercase">Suivi</div>
        <h1 className="mt-3 font-serif text-white text-4xl">Suivre une réservation</h1>
        <p className="mt-3 text-gray-400">Entrez votre référence, par exemple <b className="gold">DSA-ABC123</b>.</p>

        <form onSubmit={onSubmit} className="mt-8 flex gap-3">
          <input
            data-testid="track-input-ref"
            className="flex-1 bg-black border border-white/10 focus:border-[color:var(--gold)] px-4 py-3 text-white focus:outline-none tracking-[0.2em]"
            placeholder="DSA-XXXXXX"
            value={ref}
            onChange={(e) => setRef(e.target.value)}
          />
          <button data-testid="track-btn-search" type="submit" className="btn-gold inline-flex items-center gap-2">
            <Search size={16} /> Rechercher
          </button>
        </form>

        {loading && <div className="mt-10 text-gray-400">Chargement...</div>}

        {data && (
          <div className="mt-10 bg-[#0d0d0d] border border-white/10 p-8" data-testid="track-result">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500">Référence</div>
                <div className="font-serif text-3xl text-white">{data.reference}</div>
              </div>
              <div
                className={`px-3 py-1 border text-xs uppercase tracking-widest ${STATUS_STYLES[data.status] || "text-gray-300 border-white/10"}`}
                data-testid="track-status"
              >
                {statusLabel(data.status)}
              </div>
            </div>

            <div className="mt-6 grid sm:grid-cols-2 gap-6">
              <Row label="Prestation" value={kindLabel(data.booking_type)} />
              <Row label="Client" value={data.client_name} />
              <Row label="Montant total" value={eur(data.price.total)} />
              <Row
                label={data.price.payment_mode === "full" ? "Payé" : "Acompte réglé"}
                value={eur(data.price.deposit_amount)}
              />
              {data.price.payment_mode !== "full" && (
                <Row label="Solde restant" value={eur(data.price.total - data.price.deposit_amount)} />
              )}
              <Row label="Paiement" value={statusLabel(data.payment_status)} />
            </div>

            {data.status === "refused" && data.refuse_reason && (
              <div className="mt-6 border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-200">
                Motif : {data.refuse_reason}
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                data-testid="btn-download-devis"
                href={`${API}/documents/${data.reference}/devis.pdf`}
                target="_blank"
                rel="noreferrer"
                className="btn-outline-gold inline-flex items-center gap-2"
              >
                <FileDown size={16} /> Télécharger le devis
              </a>
              {(data.status === "confirmed" || data.status === "completed" || data.payment_status === "paid") && (
                <a
                  data-testid="btn-download-facture"
                  href={`${API}/documents/${data.reference}/facture.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-gold inline-flex items-center gap-2"
                >
                  <FileDown size={16} /> Télécharger la facture
                </a>
              )}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className="text-white mt-1">{value}</div>
    </div>
  );
}
